import * as React from 'react';
import log from 'utils/log';
import { reportPlaybackState, type TMediaPlaybackKind } from 'utils/ui/mediaPlaybackSink';

/** Activation-bearing gestures on every input type (HTML "activation triggering input event"). */
const RESTORE_GESTURES = ['pointerup', 'touchend', 'keydown'] as const;

/** Which kind of element the ladder is driving, straight off the tag — `<Video>` or `<AudioSink>`. */
function playbackKind(element: HTMLMediaElement): TMediaPlaybackKind {
  return element.tagName === 'AUDIO' ? 'audio' : 'video';
}

export interface IMediaPlayback {
  /** True while autoplay was rejected (`NotAllowedError`) and the element is force-muted until the next user gesture. */
  forcedMute: boolean;
  /** Explicit `play()` on the element — the source of truth for playback; the `autoPlay` attribute alone is not. */
  play: () => void;
  /** `onPause` handler: re-kicks a spurious pause while the source is still live. */
  handlePause: (event: React.SyntheticEvent<HTMLMediaElement>) => void;
}

/**
 * AVS-4302 takeover reliability (Safari) — the playback ladder for ONE media element, shared by
 * `<Video>` and `<AudioSink>` (AVS-4397 moved remote audio out of the tile into its own leaf, and
 * both leaves need the identical ladder rather than two copies of it).
 *
 * The `autoPlay` attribute is not enough: Safari does not reliably start playback for a
 * `srcObject` assigned after mount, and rejects UNMUTED MediaStream playback that starts long
 * after the triggering gesture (takeover negotiation takes seconds). The ladder:
 *   explicit `play()` after attach → retry on `loadedmetadata` → `NotAllowedError` falls back to
 *   MUTED playback and the next user gesture lifts the mute → `AbortError` (source swapped
 *   mid-play; `ontrack` rebuilds streams in bursts) is superseded by the next attempt.
 * Safari can also silently pause a live element (tab visibility flips, re-parenting under a
 * crossfade); there is no user pause UI, so a pause with a live source is always spurious.
 * On unmount the element is severed from its stream: React does not `pause()` a removed media
 * element, and a detached one keeps playing audio until it is garbage-collected.
 *
 * @param ref the element the ladder drives (captured at mount for the unmount severing)
 * @param onGestureRestore runs INSIDE the next user gesture after a forced mute, before the
 *   replay — the caller resets `muted` there, because WebKit only honours the unmute while the
 *   gesture is current. The state update that follows makes the rendered prop match it.
 */
export const useMediaPlayback = (
  ref: React.RefObject<HTMLMediaElement | null>,
  onGestureRestore?: () => void
): IMediaPlayback => {
  const [forcedMute, setForcedMute] = React.useState(false);

  // Latest-callback ref: the gesture listener is registered once per forced mute and must read
  // the caller's CURRENT mute state without re-registering on every render.
  const restoreRef = React.useRef(onGestureRestore);
  React.useEffect(() => {
    restoreRef.current = onGestureRestore;
  });

  // Final review W3: which kind was actually forced-muted, captured HERE rather than re-derived
  // from `ref.current` at gesture time. The gesture can arrive after the tile's own DOM node is
  // gone (a conditional render swap the OWNING component survives — this hook instance, and its
  // gesture listener, stays mounted), and `setForcedMute(false)` below is unconditional; gating
  // ONLY the report on `ref.current` still being live left a recording showing `forced-mute` with
  // no matching `restored`, reading as "audio stayed muted for the rest of the call" when it did
  // not.
  const forcedMuteKindRef = React.useRef<TMediaPlaybackKind | null>(null);

  const attemptPlay = React.useCallback((element: HTMLMediaElement | null) => {
    if (!element || (!element.srcObject && !element.src)) return;
    const kind = playbackKind(element);
    element.play().catch((error: unknown) => {
      const name = (error as { name?: string } | null)?.name;
      if (name === 'AbortError') return;
      if (name === 'NotAllowedError') {
        forcedMuteKindRef.current = kind;
        setForcedMute(true);
        reportPlaybackState(kind, 'forced-mute');
        return;
      }
      log.warn('[Video] play() failed', name, error);
    });
  }, []);

  const play = React.useCallback(() => attemptPlay(ref.current), [attemptPlay, ref]);

  // Forced-mute recovery: retry muted (allowed everywhere, frames render / the element stays
  // "playing"), then lift the mute on the NEXT user gesture. The gesture must carry user
  // activation, or the unmuted play() is rejected again and the mute silently re-arms forever:
  // per the HTML activation rules a touch `pointerdown` does NOT activate (only a mouse one does),
  // a touch tap activates on `pointerup` / `touchend`, a mouse click on `pointerup` (its
  // `mousedown` activation is still current), a key on `keydown`. AVS-4397 review: on the phone
  // — the headline scenario of the root sinks — a `pointerdown`-only listener left the dealer
  // deaf after a blocked autoplay. A single tap fires more than one of these, so the restore
  // runs once per arming.
  React.useEffect(() => {
    if (!forcedMute) return undefined;
    play();
    let restored = false;
    const restore = () => {
      if (restored) return;
      restored = true;
      restoreRef.current?.();
      play();
      setForcedMute(false);
      const kind = forcedMuteKindRef.current;
      forcedMuteKindRef.current = null;
      if (kind) {
        reportPlaybackState(kind, 'restored');
      }
    };
    const options = { once: true, capture: true } as const;
    for (const type of RESTORE_GESTURES) {
      document.addEventListener(type, restore, options);
    }
    return () => {
      for (const type of RESTORE_GESTURES) {
        document.removeEventListener(type, restore, { capture: true });
      }
    };
  }, [forcedMute, play, ref]);

  const handlePause = React.useCallback(
    (event: React.SyntheticEvent<HTMLMediaElement>) => {
      const element = event.currentTarget;
      if (!element || (!element.srcObject && !element.src) || element.ended) return;
      if (document.visibilityState === 'hidden') return;
      attemptPlay(element);
    },
    [attemptPlay]
  );

  React.useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') play();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [play]);

  // Captured at MOUNT: React detaches refs before passive cleanups run, so reading `ref.current`
  // inside the cleanup would find null and leave the element playing detached.
  React.useEffect(() => {
    const element = ref.current;
    return () => {
      if (element) {
        element.srcObject = null;
      }
    };
  }, [ref]);

  return { forcedMute, play, handlePause };
};
